/**
 * ReportCharts Component
 *
 * Visualiza los datos del reporte en gráficas interactivas.
 * Incluye: ROI Timeline, Cost Breakdown, Savings Projection,
 * Pain Points Distribution, e Implementation Timeline.
 */

import React from 'react';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import {
  ROICalculation,
  ImplementationRoadmap,
  BusinessIntelligence
} from '../../../types';
import styles from './ReportCharts.module.css';

// ============================================
// TIPOS
// ============================================

interface ReportChartsProps {
  roiCalculation?: ROICalculation;
  implementationRoadmap?: ImplementationRoadmap;
  businessIntelligence?: BusinessIntelligence;
}

// ============================================
// COLORES
// ============================================

const COLORS = {
  primary: '#0ea5e9',
  secondary: '#6366f1',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  gray: '#6b7280',
  critical: '#dc2626',
  high: '#f97316',
  medium: '#eab308',
  low: '#22c55e'
};

// ============================================
// UTILIDADES
// ============================================

const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('es-CR').format(Math.round(value));
};

// ============================================
// GRÁFICA 1: ROI TIMELINE
// ============================================

interface ROITimelineChartProps {
  data: ROICalculation;
}

const ROITimelineChart: React.FC<ROITimelineChartProps> = ({ data }) => {
  const { cost_breakdown, savings_projection, payback_period_months } = data;
  const initialInvestment = cost_breakdown.total_cost;
  const monthlySavings = savings_projection.total_monthly_savings;

  const chartData = [];
  for (let month = 0; month <= 36; month++) {
    const cumulativeSavings = month * monthlySavings;
    const netValue = cumulativeSavings - initialInvestment;
    chartData.push({
      month,
      netValue: Math.round(netValue),
      label: month === 0 ? 'Inicio' : `Mes ${month}`
    });
  }

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartIcon}>📈</span>
        ROI Timeline - Inversión vs Retorno
      </h3>
      <p className={styles.chartDescription}>
        Proyección de retorno de inversión a 3 años. El punto de equilibrio se alcanza en el mes {Math.round(payback_period_months)}.
      </p>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            <linearGradient id="colorPositive" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3}/>
              <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="month"
            tickFormatter={(value) => value === 0 ? 'Inicio' : `${value}`}
            stroke="#6b7280"
            fontSize={12}
          />
          <YAxis
            tickFormatter={formatCurrency}
            stroke="#6b7280"
            fontSize={12}
          />
          <Tooltip
            formatter={(value: number | undefined) => [formatCurrency(value ?? 0), 'Valor Neto']}
            labelFormatter={(label) => `Mes ${label}`}
          />
          <ReferenceLine
            y={0}
            stroke="#374151"
            strokeWidth={2}
            strokeDasharray="5 5"
          />
          <ReferenceLine
            x={Math.round(payback_period_months)}
            stroke={COLORS.warning}
            strokeWidth={2}
            strokeDasharray="5 5"
          />
          <Area
            type="monotone"
            dataKey="netValue"
            stroke={COLORS.primary}
            strokeWidth={3}
            fill="url(#colorPositive)"
          />
        </AreaChart>
      </ResponsiveContainer>
      <div className={styles.chartMetrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Inversión Inicial</span>
          <span className={styles.metricValue} style={{ color: COLORS.danger }}>
            -{formatCurrency(initialInvestment)}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Breakeven</span>
          <span className={styles.metricValue} style={{ color: COLORS.warning }}>
            Mes {Math.round(payback_period_months)}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Retorno Año 3</span>
          <span className={styles.metricValue} style={{ color: COLORS.success }}>
            +{formatCurrency((36 * monthlySavings) - initialInvestment)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// GRÁFICA 2: COST BREAKDOWN
// ============================================

interface CostBreakdownChartProps {
  data: ROICalculation;
}

const CostBreakdownChart: React.FC<CostBreakdownChartProps> = ({ data }) => {
  const { cost_breakdown } = data;
  const hourlyRate = cost_breakdown.hourly_rate || 75;

  const chartData = [
    { name: 'Discovery', hours: cost_breakdown.discovery_hours, cost: cost_breakdown.discovery_hours * hourlyRate, fill: '#3b82f6' },
    { name: 'Design', hours: cost_breakdown.design_hours, cost: cost_breakdown.design_hours * hourlyRate, fill: '#8b5cf6' },
    { name: 'Development', hours: cost_breakdown.development_hours, cost: cost_breakdown.development_hours * hourlyRate, fill: '#06b6d4' },
    { name: 'Testing', hours: cost_breakdown.testing_hours, cost: cost_breakdown.testing_hours * hourlyRate, fill: '#10b981' },
    { name: 'Deployment', hours: cost_breakdown.deployment_hours, cost: cost_breakdown.deployment_hours * hourlyRate, fill: '#f59e0b' },
    { name: 'Training', hours: cost_breakdown.training_hours, cost: cost_breakdown.training_hours * hourlyRate, fill: '#ec4899' }
  ].filter(item => item.hours > 0);

  if (cost_breakdown.infrastructure_cost) {
    chartData.push({ name: 'Infraestructura', hours: 0, cost: cost_breakdown.infrastructure_cost, fill: '#6366f1' });
  }
  if (cost_breakdown.third_party_tools) {
    chartData.push({ name: 'Herramientas', hours: 0, cost: cost_breakdown.third_party_tools, fill: '#14b8a6' });
  }

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartIcon}>💰</span>
        Desglose de Inversión por Fase
      </h3>
      <p className={styles.chartDescription}>
        Distribución de {formatNumber(cost_breakdown.total_hours)} horas totales ({formatCurrency(cost_breakdown.total_cost)}).
      </p>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 20, right: 30, left: 80, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={true} vertical={false} />
          <XAxis type="number" tickFormatter={formatCurrency} stroke="#6b7280" fontSize={12} />
          <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={12} width={70} />
          <Tooltip formatter={(value: number | undefined) => [formatCurrency(value ?? 0), 'Costo']} />
          <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div className={styles.chartLegend}>
        {chartData.map((item) => (
          <div key={item.name} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ backgroundColor: item.fill }} />
            <span className={styles.legendLabel}>{item.name}</span>
            <span className={styles.legendValue}>
              {item.hours > 0 ? `${item.hours}h · ` : ''}{formatCurrency(item.cost)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// GRÁFICA 3: SAVINGS PROJECTION
// ============================================

interface SavingsProjectionChartProps {
  data: ROICalculation;
}

const SavingsProjectionChart: React.FC<SavingsProjectionChartProps> = ({ data }) => {
  const { savings_projection } = data;
  const monthlyTimeSavings = savings_projection.monthly_time_savings_usd;
  const monthlyErrorSavings = savings_projection.monthly_error_savings_usd || 0;

  const chartData = [];
  for (let month = 1; month <= 12; month++) {
    chartData.push({
      month: `Mes ${month}`,
      timeSavings: Math.round(monthlyTimeSavings * month),
      errorSavings: Math.round(monthlyErrorSavings * month),
      total: Math.round((monthlyTimeSavings + monthlyErrorSavings) * month)
    });
  }

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartIcon}>💵</span>
        Proyección de Ahorros (Primer Año)
      </h3>
      <p className={styles.chartDescription}>
        Ahorro acumulado de {formatCurrency(savings_projection.total_annual_savings)} en el primer año.
      </p>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
          <defs>
            <linearGradient id="colorTime" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0.1}/>
            </linearGradient>
            <linearGradient id="colorError" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.8}/>
              <stop offset="95%" stopColor={COLORS.success} stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="month" stroke="#6b7280" fontSize={12} tickFormatter={(value) => value.replace('Mes ', '')} />
          <YAxis tickFormatter={formatCurrency} stroke="#6b7280" fontSize={12} />
          <Tooltip
            formatter={(value: number | undefined, name: string | undefined) => {
              const labels: Record<string, string> = { timeSavings: 'Ahorro de Tiempo', errorSavings: 'Reducción de Errores' };
              const key = name ?? '';
              return [formatCurrency(value ?? 0), labels[key] || key];
            }}
          />
          <Legend formatter={(value) => value === 'timeSavings' ? 'Ahorro de Tiempo' : 'Reducción de Errores'} />
          <Area type="monotone" dataKey="timeSavings" stackId="1" stroke={COLORS.primary} fill="url(#colorTime)" strokeWidth={2} />
          {monthlyErrorSavings > 0 && (
            <Area type="monotone" dataKey="errorSavings" stackId="1" stroke={COLORS.success} fill="url(#colorError)" strokeWidth={2} />
          )}
        </AreaChart>
      </ResponsiveContainer>
      <div className={styles.chartMetrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Ahorro Mensual</span>
          <span className={styles.metricValue} style={{ color: COLORS.primary }}>
            {formatCurrency(savings_projection.total_monthly_savings)}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Horas Ahorradas/Mes</span>
          <span className={styles.metricValue} style={{ color: COLORS.secondary }}>
            {formatNumber(savings_projection.hours_saved_monthly)}h
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Ahorro Anual</span>
          <span className={styles.metricValue} style={{ color: COLORS.success }}>
            {formatCurrency(savings_projection.total_annual_savings)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================
// GRÁFICA 4: PAIN POINTS DISTRIBUTION
// ============================================

interface PainPointsChartProps {
  data: BusinessIntelligence;
}

const PainPointsChart: React.FC<PainPointsChartProps> = ({ data }) => {
  const painPoints = data.pain_points || [];

  const severityCounts: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
  painPoints.forEach(pp => {
    const severity = (pp.severity || 'medium').toLowerCase();
    if (severity in severityCounts) {
      severityCounts[severity]++;
    } else {
      severityCounts['medium']++;
    }
  });

  const chartData = [
    { name: 'Crítico', value: severityCounts.critical, color: COLORS.critical },
    { name: 'Alto', value: severityCounts.high, color: COLORS.high },
    { name: 'Medio', value: severityCounts.medium, color: COLORS.medium },
    { name: 'Bajo', value: severityCounts.low, color: COLORS.low }
  ].filter(item => item.value > 0);

  const total = painPoints.length;
  if (total === 0) return null;

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartIcon}>🎯</span>
        Distribución de Problemas Identificados
      </h3>
      <p className={styles.chartDescription}>
        Se identificaron {total} problemas/pain points en el análisis del negocio.
      </p>
      <div className={styles.pieChartWrapper}>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey="value"
              label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
              labelLine={true}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip formatter={(value: number | undefined) => [value ?? 0, 'Problemas']} />
          </PieChart>
        </ResponsiveContainer>
        <div className={styles.pieCenter}>
          <span className={styles.pieCenterValue}>{total}</span>
          <span className={styles.pieCenterLabel}>Total</span>
        </div>
      </div>
      <div className={styles.chartLegend}>
        {chartData.map((item) => (
          <div key={item.name} className={styles.legendItem}>
            <span className={styles.legendDot} style={{ backgroundColor: item.color }} />
            <span className={styles.legendLabel}>{item.name}</span>
            <span className={styles.legendValue}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// GRÁFICA 5: IMPLEMENTATION TIMELINE
// ============================================

interface ImplementationTimelineChartProps {
  data: ImplementationRoadmap;
}

const ImplementationTimelineChart: React.FC<ImplementationTimelineChartProps> = ({ data }) => {
  const { milestones, total_duration_weeks, quick_wins } = data;

  let currentWeek = 0;
  const chartData = milestones.map((milestone) => {
    const start = currentWeek;
    currentWeek += milestone.duration_weeks;
    return {
      name: milestone.name,
      phase: milestone.phase,
      start,
      duration: milestone.duration_weeks
    };
  });

  const phaseColors: Record<string, string> = {
    discovery: '#3b82f6',
    design: '#8b5cf6',
    development: '#06b6d4',
    testing: '#10b981',
    deployment: '#f59e0b',
    training: '#ec4899',
    support: '#6366f1'
  };

  return (
    <div className={styles.chartContainer}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartIcon}>📅</span>
        Timeline de Implementación
      </h3>
      <p className={styles.chartDescription}>
        Duración total: {total_duration_weeks} semanas ({Math.round(total_duration_weeks / 4)} meses).
        {quick_wins && quick_wins.length > 0 && ` Incluye ${quick_wins.length} quick wins.`}
      </p>
      <ResponsiveContainer width="100%" height={Math.max(250, chartData.length * 45)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 20, right: 30, left: 120, bottom: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
          <XAxis type="number" domain={[0, total_duration_weeks]} tickFormatter={(value) => `Sem ${value}`} stroke="#6b7280" fontSize={12} />
          <YAxis type="category" dataKey="name" stroke="#6b7280" fontSize={11} width={110} />
          <Tooltip formatter={(value: number | undefined) => [`${value ?? 0} semanas`, 'Duración']} labelFormatter={(label) => `Fase: ${label}`} />
          <Bar dataKey="duration" radius={[0, 4, 4, 0]} barSize={24}>
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={phaseColors[entry.phase] || COLORS.primary} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {quick_wins && quick_wins.length > 0 && (
        <div className={styles.quickWins}>
          <h4 className={styles.quickWinsTitle}>⚡ Quick Wins</h4>
          <div className={styles.quickWinsList}>
            {quick_wins.slice(0, 4).map((qw, index) => (
              <div key={index} className={styles.quickWinItem}>
                <span className={styles.quickWinWeek}>Sem {qw.delivery_week}</span>
                <span className={styles.quickWinName}>{qw.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================

export const ReportCharts: React.FC<ReportChartsProps> = ({
  roiCalculation,
  implementationRoadmap,
  businessIntelligence
}) => {
  if (!roiCalculation && !implementationRoadmap && !businessIntelligence) {
    return null;
  }

  return (
    <div className={styles.chartsSection}>
      <h2 className={styles.sectionTitle}>
        <span className={styles.sectionIcon}>📊</span>
        Visualización de Datos
      </h2>

      <div className={styles.chartsGrid}>
        {roiCalculation && <ROITimelineChart data={roiCalculation} />}
        {roiCalculation && <CostBreakdownChart data={roiCalculation} />}
        {roiCalculation && <SavingsProjectionChart data={roiCalculation} />}
        {businessIntelligence && businessIntelligence.pain_points && businessIntelligence.pain_points.length > 0 && (
          <PainPointsChart data={businessIntelligence} />
        )}
        {implementationRoadmap && implementationRoadmap.milestones && implementationRoadmap.milestones.length > 0 && (
          <ImplementationTimelineChart data={implementationRoadmap} />
        )}
      </div>
    </div>
  );
};

export default ReportCharts;
